import { useCallback, useEffect, useState } from "react";
import { adminFetch } from "../lib/api";
import type {
  ChannelSettingResponse,
  ChannelSettingUpdate,
  ChannelType,
  TestSendResult,
} from "../types/subscriptionSettings";

const BASE = "/api/v1/admin/subscription-settings";

export interface UseChannelSettings {
  settings: Record<ChannelType, ChannelSettingResponse> | null;
  isLoading: boolean;
  error: string | null;
  fieldErrors: Record<ChannelType, Record<string, string[]>>;
  refetch: () => Promise<void>;
  save: (channel: ChannelType, payload: ChannelSettingUpdate) => Promise<ChannelSettingResponse | null>;
  test: (channel: ChannelType, destination: string) => Promise<TestSendResult>;
}

const EMPTY_FIELD_ERRORS: Record<ChannelType, Record<string, string[]>> = {
  email: {},
  sms: {},
  slack: {},
  teams: {},
  webhook: {},
};

export function useChannelSettings(): UseChannelSettings {
  const [settings, setSettings] = useState<Record<ChannelType, ChannelSettingResponse> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<ChannelType, Record<string, string[]>>>(EMPTY_FIELD_ERRORS);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`${BASE}/channels/`);
      if (res.status === 403) {
        setError("Access denied: Subscription Settings is restricted to super-admins.");
        setSettings(null);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const list = (await res.json()) as ChannelSettingResponse[];
      const byType = list.reduce(
        (acc, item) => ({ ...acc, [item.channel_type]: item }),
        {} as Record<ChannelType, ChannelSettingResponse>,
      );
      setSettings(byType);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load settings.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const save = useCallback<UseChannelSettings["save"]>(
    async (channel, payload) => {
      setFieldErrors((prev) => ({ ...prev, [channel]: {} }));
      const res = await adminFetch(`${BASE}/channels/${channel}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.status === 400) {
        const errs = (await res.json()) as Record<string, string[]>;
        setFieldErrors((prev) => ({ ...prev, [channel]: errs }));
        return null;
      }
      if (!res.ok) {
        setError(`Save failed: HTTP ${res.status}`);
        return null;
      }
      const updated = (await res.json()) as ChannelSettingResponse;
      setSettings((prev) => (prev ? { ...prev, [channel]: updated } : prev));
      return updated;
    },
    [],
  );

  const test = useCallback<UseChannelSettings["test"]>(
    async (channel, destination) => {
      const res = await adminFetch(`${BASE}/channels/${channel}/test/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ destination }),
      });
      if (res.status === 429) {
        return { success: false, latency_ms: 0, provider_response: "Rate limit: 10 test sends per minute." };
      }
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        return { success: false, latency_ms: 0, provider_response: (body as { error?: string }).error ?? "Channel not ready." };
      }
      if (!res.ok) {
        return { success: false, latency_ms: 0, provider_response: `HTTP ${res.status}` };
      }
      return (await res.json()) as TestSendResult;
    },
    [],
  );

  return { settings, isLoading, error, fieldErrors, refetch, save, test };
}
